import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col, Spinner, ProgressBar, Badge } from 'react-bootstrap';
import { FaMagic, FaCheckCircle, FaFileAlt, FaEdit } from 'react-icons/fa';
import axiosInstance from 'helpers/api';

interface DocumentPrefillModalProps {
  show: boolean;
  onHide: () => void;
  transactionId: string;
  documents: Array<{
    id: string;
    name?: string;
    filename?: string;
    original_filename?: string;
    type?: string;
    anvil_template_id?: string;
    is_anvil_template?: boolean;
  }>;
  onDocumentsPrefilled?: (documents: any[]) => void;
}

interface PrefillData {
  [key: string]: string | number;
}

const DocumentPrefillModal: React.FC<DocumentPrefillModalProps> = ({
  show,
  onHide,
  transactionId,
  documents,
  onDocumentsPrefilled
}) => {
  const [loading, setLoading] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [transactionData, setTransactionData] = useState<any>(null);
  const [prefillData, setPrefillData] = useState<PrefillData>({});
  const [prefilledDocs, setPrefilledDocs] = useState<any[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [templateFields, setTemplateFields] = useState<any[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  // Common fields that might need editing
  const defaultEditableFields = [
    { key: 'purchase_price', label: 'Purchase Price', type: 'number' },
    { key: 'earnest_money', label: 'Earnest Money', type: 'number' },
    { key: 'closing_date', label: 'Closing Date', type: 'date' },
    { key: 'inspection_deadline', label: 'Inspection Deadline', type: 'date' },
    { key: 'financing_deadline', label: 'Financing Deadline', type: 'date' },
    { key: 'commission_percentage', label: 'Commission %', type: 'text' }
  ];

  useEffect(() => {
    if (show && transactionId) {
      prepareDocuments();
    }
  }, [show, transactionId]);

  // Fetch template fields when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      fetchTemplateFields(selectedTemplate);
    } else {
      setTemplateFields([]);
    }
  }, [selectedTemplate]);

  const prepareDocuments = async () => {
    try {
      setPreparing(true);
      setError(null);
      setProgress(10);

      // Get transaction data and prepare fillable documents
      const response = await axiosInstance.get(`/transaction-prefill/${transactionId}/prepare`);
      
      setTransactionData(response.data.transaction);
      setProgress(50);

      // Initialize prefill data with transaction data
      const initialData: PrefillData = {
        purchase_price: response.data.transaction.purchase_price || '',
        earnest_money: response.data.transaction.earnest_money || '',
        closing_date: response.data.transaction.closing_date || new Date().toISOString().split('T')[0],
        inspection_deadline: calculateDeadline(10),
        financing_deadline: calculateDeadline(21),
        commission_percentage: '3',
        // Add party data
        buyer_name: response.data.transaction.parties?.find((p: any) => p.party_type === 'buyer')?.name || response.data.transaction.client_name || '',
        buyer_email: response.data.transaction.parties?.find((p: any) => p.party_type === 'buyer')?.email || response.data.transaction.client_email || '',
        seller_name: response.data.transaction.parties?.find((p: any) => p.party_type === 'seller')?.name || '',
        seller_email: response.data.transaction.parties?.find((p: any) => p.party_type === 'seller')?.email || '',
        property_address: response.data.transaction.property?.address || response.data.transaction.property_address || '',
      };

      setPrefillData(initialData);
      
      // Set all documents as selected by default
      setSelectedDocuments(documents.map(doc => doc.id));
      
      // If there's an Anvil template, select it by default
      const anvilDoc = documents.find(doc => doc.is_anvil_template && doc.anvil_template_id);
      if (anvilDoc && anvilDoc.anvil_template_id) {
        setSelectedTemplate(anvilDoc.anvil_template_id);
      }

      setProgress(100);
      setPreparing(false);
    } catch (error) {
      console.error('Error preparing documents:', error);
      setError('Failed to prepare documents for pre-filling');
      setPreparing(false);
    }
  };

  const calculateDeadline = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  const fetchTemplateFields = async (templateId: string) => {
    try {
      setLoadingFields(true);
      const response = await axiosInstance.get(`/api/anvil/template/${templateId}/fields`);
      
      if (response.data.success && response.data.fields) {
        setTemplateFields(response.data.fields);
        
        // Initialize prefill data with template field names
        const newPrefillData = { ...prefillData };
        response.data.fields.forEach((field: any) => {
          const fieldKey = field.name.toLowerCase().replace(/\s+/g, '_');
          if (!newPrefillData[fieldKey]) {
            // Set default values based on transaction data
            if (fieldKey.includes('buyer_name')) {
              newPrefillData[fieldKey] = transactionData?.parties?.find((p: any) => p.party_type === 'buyer')?.name || transactionData?.client_name || '';
            } else if (fieldKey.includes('buyer_email')) {
              newPrefillData[fieldKey] = transactionData?.parties?.find((p: any) => p.party_type === 'buyer')?.email || transactionData?.client_email || '';
            } else if (fieldKey.includes('property_address')) {
              newPrefillData[fieldKey] = transactionData?.property?.address || transactionData?.property_address || '';
            } else if (fieldKey.includes('purchase_price')) {
              newPrefillData[fieldKey] = transactionData?.purchase_price || '';
            }
          }
        });
        setPrefillData(newPrefillData);
      }
    } catch (error) {
      console.error('Error fetching template fields:', error);
    } finally {
      setLoadingFields(false);
    }
  };

  const handlePreview = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Generate preview using the selected template and data
      const response = await axiosInstance.post(`/api/anvil-preview/template/generate`, {
        templateId: selectedTemplate,
        data: prefillData,
        transaction_id: transactionId
      });
      
      if (response.data.success && response.data.previewUrl) {
        setPreviewUrl(response.data.previewUrl);
        setShowPreview(true);
      } else {
        setError('Failed to generate preview');
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      setError('Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handlePrefillAll = async () => {
    try {
      setLoading(true);
      setError(null);
      setProgress(0);
      
      const results = [];
      const docsToFill = documents.filter(doc => selectedDocuments.includes(doc.id));
      const totalDocs = docsToFill.length;

      if (totalDocs === 0) {
        setError('Please select at least one document to pre-fill');
        setLoading(false);
        return;
      }

      for (let i = 0; i < docsToFill.length; i++) {
        const doc = docsToFill[i];
        setProgress((i / totalDocs) * 100);

        try {
          const response = await axiosInstance.post(`/transaction-prefill/${transactionId}/fill`, {
            documentId: doc.id,
            templateId: selectedTemplate || doc.anvil_template_id,
            additionalData: prefillData
          });

          results.push({
            ...doc,
            status: 'success',
            prefilledUrl: response.data.prefilledUrl,
            fieldsPopulated: response.data.fieldsPopulated
          });
        } catch (docError) {
          console.error(`Error prefilling ${doc.name || doc.original_filename}:`, docError);
          results.push({
            ...doc,
            status: 'error',
            error: 'Failed to pre-fill document'
          });
        }
      }

      setProgress(100);
      setPrefilledDocs(results);
      
      // Notify parent
      if (onDocumentsPrefilled) {
        onDocumentsPrefilled(results.filter(r => r.status === 'success'));
      }

      // Show success for 2 seconds then close
      setTimeout(() => {
        onHide();
      }, 2000);

    } catch (error) {
      console.error('Error pre-filling documents:', error);
      setError('Failed to pre-fill some documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (key: string, value: string | number) => {
    setPrefillData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaMagic className="me-2 text-primary" />
            Pre-fill Transaction Documents
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
        {preparing ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p>Preparing documents for pre-filling...</p>
            <ProgressBar now={progress} animated />
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : prefilledDocs.length > 0 ? (
          <div>
            <Alert variant="success">
              <FaCheckCircle className="me-2" />
              Successfully pre-filled {prefilledDocs.filter(d => d.status === 'success').length} of {prefilledDocs.length} documents!
            </Alert>
            <div className="document-results">
              {prefilledDocs.map((doc, index) => (
                <div key={index} className="d-flex align-items-center mb-2">
                  <FaFileAlt className={`me-2 ${doc.status === 'success' ? 'text-success' : 'text-danger'}`} />
                  <span>{doc.name || doc.original_filename || doc.filename}</span>
                  {doc.status === 'success' && doc.fieldsPopulated && (
                    <small className="text-muted ms-2">({doc.fieldsPopulated} fields filled)</small>
                  )}
                  {doc.status === 'error' && (
                    <small className="text-danger ms-2">(Failed)</small>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <Alert variant="info">
              <FaMagic className="me-2" />
              Review and adjust the data below before pre-filling {documents.length} document{documents.length > 1 ? 's' : ''}.
            </Alert>

            {/* Template Selection */}
            {documents.some(doc => doc.is_anvil_template) && (
              <div className="mb-4">
                <Form.Group>
                  <Form.Label>
                    <FaMagic className="me-2 text-primary" />
                    <strong>Select Template to Use</strong>
                  </Form.Label>
                  <Form.Select 
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="mb-2"
                  >
                    <option value="">-- Use document's own template --</option>
                    {documents.filter(doc => doc.is_anvil_template && doc.anvil_template_id).map(doc => (
                      <option key={doc.anvil_template_id} value={doc.anvil_template_id}>
                        {doc.name || doc.original_filename || doc.filename} Template
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Select a template to apply its fields to all selected documents
                  </Form.Text>
                </Form.Group>
              </div>
            )}

            {/* Document Selection */}
            <div className="mb-4">
              <h6 className="mb-3">Select Documents to Pre-fill:</h6>
              <div className="border rounded p-3">
                <Form.Check 
                  type="checkbox"
                  id="select-all"
                  label={<strong>Select All</strong>}
                  checked={selectedDocuments.length === documents.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedDocuments(documents.map(doc => doc.id));
                    } else {
                      setSelectedDocuments([]);
                    }
                  }}
                  className="mb-2"
                />
                <hr className="my-2" />
                {documents.map((doc) => (
                  <Form.Check 
                    key={doc.id}
                    type="checkbox"
                    id={`doc-${doc.id}`}
                    label={
                      <>
                        <FaFileAlt className="me-2 text-primary" />
                        {doc.name || doc.original_filename || doc.filename}
                        {doc.is_anvil_template && (
                          <Badge bg="success" className="ms-2">Fillable</Badge>
                        )}
                      </>
                    }
                    checked={selectedDocuments.includes(doc.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedDocuments([...selectedDocuments, doc.id]);
                      } else {
                        setSelectedDocuments(selectedDocuments.filter(id => id !== doc.id));
                      }
                    }}
                    className="mb-1"
                  />
                ))}
              </div>
            </div>

            {/* Show Template Fields when template is selected */}
            {loadingFields && (
              <div className="text-center my-4">
                <Spinner animation="border" size="sm" className="me-2" />
                <span className="text-muted">Loading template fields...</span>
              </div>
            )}

            {!loadingFields && templateFields.length > 0 && (
              <div className="mb-4">
                <h6 className="mb-3">
                  <FaMagic className="me-2 text-success" />
                  Anvil Template Fields
                </h6>
                <Alert variant="info" className="mb-3">
                  <small>These fields will be mapped to your documents. Review and adjust as needed.</small>
                </Alert>
                <Form>
                  <Row>
                    {templateFields.map((field, index) => {
                      const fieldKey = field.name.toLowerCase().replace(/\s+/g, '_');
                      const fieldLabel = field.name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                      
                      return (
                        <Col md={6} key={index} className="mb-3">
                          <Form.Group>
                            <Form.Label>
                              {fieldLabel}
                              {field.required && <span className="text-danger ms-1">*</span>}
                            </Form.Label>
                            <Form.Control
                              type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                              value={prefillData[fieldKey] || ''}
                              onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                              placeholder={`Enter ${fieldLabel.toLowerCase()}`}
                              required={field.required}
                            />
                            {field.description && (
                              <Form.Text className="text-muted">
                                {field.description}
                              </Form.Text>
                            )}
                          </Form.Group>
                        </Col>
                      );
                    })}
                  </Row>
                </Form>
                
                {/* Preview Button */}
                <div className="text-center mt-3">
                  <Button
                    variant="outline-success"
                    onClick={handlePreview}
                    disabled={loading}
                  >
                    <FaMagic className="me-2" />
                    Preview Pre-filled Document
                  </Button>
                </div>
              </div>
            )}

            {/* Pre-fill Data Fields - Always show for additional data */}
            <div className="mb-4">
              <h6 className="mb-3">
                <FaEdit className="me-2 text-primary" />
                {templateFields.length > 0 ? 'Additional Transaction Details' : 'Transaction Details'}
              </h6>
              <Form>
                <Row>
                  {defaultEditableFields.map(field => (
                    <Col md={6} key={field.key} className="mb-3">
                      <Form.Group>
                        <Form.Label>{field.label}</Form.Label>
                        <Form.Control
                          type={field.type}
                          value={prefillData[field.key] || ''}
                          onChange={(e) => handleFieldChange(field.key, e.target.value)}
                          placeholder={`Enter ${field.label.toLowerCase()}`}
                        />
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </Form>
            </div>

            {loading && (
              <div className="mt-3">
                <ProgressBar now={progress} animated label={`${Math.round(progress)}%`} />
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        {prefilledDocs.length === 0 && (
          <Button 
            variant="primary" 
            onClick={handlePrefillAll}
            disabled={loading || preparing}
          >
            {loading ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Pre-filling...
              </>
            ) : (
              <>
                <FaMagic className="me-2" />
                Pre-fill All Documents
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>

    {/* Preview Modal */}
    <Modal show={showPreview} onHide={() => setShowPreview(false)} size="xl" fullscreen>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaFileAlt className="me-2" />
          Document Preview
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: 0 }}>
        {previewUrl ? (
          <iframe
            src={previewUrl}
            width="100%"
            height="100%"
            style={{ border: 'none', minHeight: '80vh' }}
            title="Document Preview"
          />
        ) : (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading preview...</p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowPreview(false)}>
          Close Preview
        </Button>
        <Button
          variant="success"
          onClick={() => {
            setShowPreview(false);
            // Proceed with prefilling all documents
            handlePrefillAll();
          }}
        >
          <FaCheckCircle className="me-2" />
          Accept & Apply to Documents
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default DocumentPrefillModal;